import { ThemeEvents } from '@theme/events';

/**
 * Wraps one bundled product's variant picker. Relays that product's own
 * `variant:update` event into this item's hidden input (read by
 * bundle-add-to-cart on submit), and stops the event from bubbling further.
 *
 * Several page-level listeners (media gallery, inventory status) react to
 * `variant:update` with no product-id check, assuming exactly one product
 * per page — stopping propagation here keeps this bundled product's update
 * from being applied to the bundle page's own gallery/stock text.
 */
class BundleItemPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    event.stopPropagation();

    if (event.detail.data.newProduct) {
      // A combined-listing option connected this slot to a sibling product —
      // adopt its id so this picker keeps tracking itself (same pattern as
      // product-form.js / product-inventory.js / product-price.js).
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const input = this.querySelector('[data-bundle-item-input]');
    if (input instanceof HTMLInputElement) {
      input.value = event.detail.resource?.id ?? '';
    }

    this.dataset.available = event.detail.resource?.available ? 'true' : 'false';

    // variant-picker.js morphs in this product's own PDP markup on every
    // change (see updateVariantPicker in assets/variant-picker.js), which
    // resets the legend's last span back to the selected option value.
    // Restore the product-name label this bundle slot shows there instead.
    const title = this.dataset.productTitle;
    if (title) {
      this.querySelectorAll('.header legend span:last-child').forEach((span) => {
        span.textContent = title;
      });
    }
  };
}

if (!customElements.get('bundle-item-picker')) {
  customElements.define('bundle-item-picker', BundleItemPicker);
}
